import { Icon, type IconName } from '@mdvip/shared/components/blocks/Icon';
import { Card, CardContent, CardHeader, CardTitle } from '@mdvip/shared/components/ui/card';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { cn } from '@mdvip/shared/lib/utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMembershipCard } from '@/hooks/membership/useMembershipCard';
import { MembershipCardArt } from './MembershipCardArt';
import { MembershipCardModal } from './MembershipCardModal';

interface CardActionProps {
  icon: IconName;
  onClick: () => void;
  children: string;
}

/** The rail card's action row: leading glyph, underline on the label only, 44px touch target. */
function CardAction({ icon, onClick, children }: CardActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-11 items-center gap-2 text-left text-sm font-medium text-foreground hover:text-primary"
    >
      <Icon name={icon} size="sm" className="text-muted-foreground" />
      <span className="underline decoration-from-font underline-offset-2">{children}</span>
    </button>
  );
}

interface MembershipCardProps {
  className?: string;
}

/**
 * The member's membership, as a card in the Profile rail: a front-face preview of the real card,
 * then the two things a member does with it. The artwork is the same component the print modal
 * mounts, at a smaller width — never a separate drawing.
 *
 * Mounted inside the page's practice-rail group, which already gates the no-physician and failure
 * states for all three rail cards, so this one only handles its own member-identity read.
 */
export function MembershipCard({ className }: MembershipCardProps) {
  const { t } = useTranslation('members');
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data, isPending } = useMembershipCard();

  if (isPending || !data) {
    return <MembershipCardSkeleton className={className} />;
  }

  return (
    <>
      <Card className={cn('gap-4', className)} data-slot="card-my-membership">
        <CardHeader>
          <CardTitle className="text-sm">{t('profile.membership.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <MembershipCardArt side="front" size="small" data={data} />
          <div className="flex min-w-0 flex-col">
            <CardAction icon="shieldCheck" onClick={() => navigate('/payments')}>
              {t('profile.membership.actions.benefits')}
            </CardAction>
            <CardAction icon="print" onClick={() => setIsModalOpen(true)}>
              {t('profile.membership.actions.printCard')}
            </CardAction>
          </div>
        </CardContent>
      </Card>
      <MembershipCardModal open={isModalOpen} onClose={() => setIsModalOpen(false)} data={data} />
    </>
  );
}

/** Loading placeholder — the heading is real text; the card face and actions are placeholders. */
export function MembershipCardSkeleton({ className }: MembershipCardProps) {
  const { t } = useTranslation('members');

  return (
    <Card className={cn('gap-4', className)} data-slot="card-my-membership-skeleton" aria-busy="true">
      <CardHeader>
        <CardTitle className="text-sm">{t('profile.membership.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Skeleton className="aspect-[520/326] w-full rounded-xl" />
        <div className="flex flex-col gap-3">
          <Skeleton className="h-4 w-44" />
          <Skeleton className="h-4 w-40" />
        </div>
      </CardContent>
    </Card>
  );
}
